import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";

import { canOpenSudoPath } from "@/role";
import { useSessionStore } from "@/store/session";
import Home from "@/view/Home.vue";
import Authenticated from "@/view/Authenticated.vue";
import Cell from "@/view/Cell.vue";
import Communication from "@/view/Communication.vue";
import FullPage from "@/components/layout/FullPage.vue";
import LicenseGateRoute from "@/components/layout/LicenseGateRoute.vue";
import ConfigurationIntegration from "@/view/configuration/Integration.vue";
import ConfigurationIntegrationArcgis from "@/view/configuration/IntegrationArcgis.vue";
import ConfigurationIntegrationFieldseeker from "@/view/configuration/IntegrationFieldseeker.vue";
import ConfigurationIntegrationMapvision from "@/view/configuration/IntegrationMapvision.vue";
import ConfigurationIntegrationVectorsurv from "@/view/configuration/IntegrationVectorsurv.vue";
import ConfigurationIntegrationVectorsurvConnection from "@/view/configuration/IntegrationVectorsurvConnection.vue";
import ConfigurationIntegrationVectorsurvEquipment from "@/view/configuration/IntegrationVectorsurvEquipment.vue";
import ConfigurationIntegrationVectorsurvSync from "@/view/configuration/IntegrationVectorsurvSync.vue";
import ConfigurationIntegrationVectorsurvUsers from "@/view/configuration/IntegrationVectorsurvUsers.vue";
import ConfigurationIntegrationVeemac from "@/view/configuration/IntegrationVeemac.vue";
import ConfigurationLinks from "@/view/configuration/Links.vue";
import ConfigurationOrganization from "@/view/configuration/Organization.vue";
import ConfigurationSpecies from "@/view/configuration/Species.vue";
import ConfigurationNotifications from "@/view/configuration/Notifications.vue";
import ConfigurationTagDetail from "@/view/configuration/TagDetail.vue";
import ConfigurationTrackingDevices from "@/view/configuration/TrackingDevices.vue";
import ConfigurationPreferences from "@/view/configuration/Preferences.vue";
import ConfigurationRoot from "@/view/configuration/Root.vue";
import ConfigurationUpload from "@/view/configuration/Upload.vue";
import ConfigurationUploadDetail from "@/view/configuration/UploadDetail.vue";
import ConfigurationUploadPool from "@/view/configuration/UploadPool.vue";
import ConfigurationUploadPoolCustom from "@/view/configuration/UploadPoolCustom.vue";
import ConfigurationUploadPoolFlyover from "@/view/configuration/UploadPoolFlyover.vue";
import ConfigurationUser from "@/view/configuration/User.vue";
import ConfigurationUserAdd from "@/view/configuration/UserAdd.vue";
import ConfigurationUserEdit from "@/view/configuration/UserEdit.vue";
import Dash from "@/view/Dash.vue";
import Intelligence from "@/view/Intelligence.vue";
import InvasiveAedes from "@/view/InvasiveAedes.vue";
import Inventory from "@/view/inventory/Inventory.vue";
import InventoryProducts from "@/view/inventory/Products.vue";
import InventoryProductDetail from "@/view/inventory/ProductDetail.vue";
import InventoryCatalog from "@/view/inventory/Catalog.vue";
import InventoryCatalogDetail from "@/view/inventory/CatalogDetail.vue";
import InventoryInterventions from "@/view/inventory/Interventions.vue";
import InventoryReceive from "@/view/inventory/Receive.vue";
import InventoryMovements from "@/view/inventory/Movements.vue";
import InventoryContainers from "@/view/inventory/Containers.vue";
import InventoryLocations from "@/view/inventory/Locations.vue";
import InventoryChecks from "@/view/inventory/Checks.vue";
import InventoryCheckDetail from "@/view/inventory/CheckDetail.vue";
import InventoryReconciliation from "@/view/inventory/Reconciliation.vue";
import InventoryUnits from "@/view/inventory/Units.vue";
import InventoryMixes from "@/view/inventory/Mixes.vue";
import InventoryMixDetail from "@/view/inventory/MixDetail.vue";
import InventoryRoot from "@/view/inventory/Root.vue";
import NotFound from "@/view/NotFound.vue";
import OAuthRefreshArcgis from "@/view/OAuthRefreshArcgis.vue";
import Operations from "@/view/Operations.vue";
import MapReview from "@/view/MapReview.vue";
import Planning from "@/view/Planning.vue";
import ReviewContact from "@/view/review/Contact.vue";
import ReviewFeature from "@/view/review/Feature.vue";
import ReviewMailer from "@/view/review/Mailer.vue";
import ReviewTranscripts from "@/view/review/Transcripts.vue";
import ReviewInspectionList from "@/view/review/InspectionList.vue";
import ReviewInspectionDetail from "@/view/review/InspectionDetail.vue";
import ReviewTreatmentList from "@/view/review/TreatmentList.vue";
import ReviewTreatmentDetail from "@/view/review/TreatmentDetail.vue";
import ReviewPool from "@/view/review/Pool.vue";
import ManualPoolReview from "@/view/review/ManualPoolReview.vue";
import ReviewRoot from "@/view/review/Root.vue";
import ReviewMapvisionIndex from "@/view/review/MapvisionIndex.vue";
import ReviewMapvisionMap from "@/view/review/MapvisionMap.vue";
import ReviewMapvisionActivityCard from "@/view/review/MapvisionActivityCard.vue";
import ReviewMapvisionTypeDetail from "@/view/review/MapvisionTypeDetail.vue";
import ReviewMapvisionTypeList from "@/view/review/MapvisionTypeList.vue";
import ReviewSite from "@/view/review/Site.vue";
import SiteDetail from "@/view/SiteDetail.vue";
import SudoVisionTaskOverview from "@/view/sudo/vision-tasks/VisionTaskOverview.vue";
import SudoVisionTaskPoolCorpus from "@/view/sudo/vision-tasks/VisionTaskPoolCorpus.vue";
import SudoVisionTaskItem from "@/view/sudo/vision-tasks/VisionTaskItem.vue";
import SudoVisionTaskPositionInference from "@/view/sudo/vision-tasks/PositionInferenceAnchoring.vue";
import SudoVisionTaskReview from "@/view/sudo/vision-tasks/VisionTaskReview.vue";
import SudoTileConfirmation from "@/view/sudo/vision-tasks/TileConfirmation.vue";
import SudoCorrectFinding from "@/view/sudo/vision-tasks/CorrectFinding.vue";
import SudoConditionReview from "@/view/sudo/vision-tasks/ConditionReview.vue";
import TrapsRoot from "@/view/traps/Root.vue";
import TrapInstances from "@/view/traps/Instances.vue";
import TrapInstance from "@/view/traps/TrapInstance.vue";
import TrapStations from "@/view/traps/Stations.vue";
import TrapStationDetail from "@/view/traps/StationDetail.vue";
import Configurations from "@/view/traps/Configurations.vue";
import TrapSampleLabels from "@/view/traps/SampleLabels.vue";
import TrapDevices from "@/view/traps/Devices.vue";
import TrapStatus from "@/view/traps/Status.vue";
import Signin from "@/view/Signin.vue";
import Signout from "@/view/Signout.vue";
import Signup from "@/view/Signup.vue";
import ForgotPassword from "@/view/ForgotPassword.vue";
import ResetPassword from "@/view/ResetPassword.vue";
import Vision from "@/view/sudo/Vision.vue";
import UI from "@/view/UI.vue";
import Voice from "@/view/Voice.vue";
import VoiceHelp from "@/view/VoiceHelp.vue";
import { apiClient } from "@/client";

import { ROUTE_NAMES } from "@/route/name";

const routes: RouteRecordRaw[] = [
	{
		path: "/",
		name: "Home",
		component: Home,
	},
	{
		children: [
			{
				component: Cell,
				name: ROUTE_NAMES.CELL_DETAIL,
				path: "/_/cell/:cell",
				props: true,
			},
			{
				path: "/_/communication",
				component: LicenseGateRoute,
				meta: { license: "core" },
				children: [
					{
						path: "",
						name: "Communication",
						component: Communication,
					},
				],
			},
			{
				path: "/_/configuration",
				name: "Configuration",
				component: ConfigurationRoot,
			},
			{
				path: "/_/configuration",
				name: "Configuration",
				component: ConfigurationRoot,
			},
			{
				path: "/_/configuration/integration",
				name: "Integration Configuration",
				component: ConfigurationIntegration,
			},
			{
				path: "/_/configuration/integration/arcgis",
				name: "Arcgis Integration Configuration",
				component: ConfigurationIntegrationArcgis,
			},
			{
				path: "/_/configuration/integration/fieldseeker",
				name: "Fieldseeker Integration Configuration",
				component: ConfigurationIntegrationFieldseeker,
			},
			{
				path: "/_/configuration/integration/mapvision",
				name: "Mapvision Integration Configuration",
				component: ConfigurationIntegrationMapvision,
			},
			{
				path: "/_/configuration/integration/vectorsurv",
				name: "Vectorsurv Integration Configuration",
				component: ConfigurationIntegrationVectorsurv,
			},
			{
				path: "/_/configuration/integration/vectorsurv/connection",
				name: "Vectorsurv Connection",
				component: ConfigurationIntegrationVectorsurvConnection,
			},
			{
				path: "/_/configuration/integration/vectorsurv/users",
				name: "Vectorsurv Users",
				component: ConfigurationIntegrationVectorsurvUsers,
			},
			{
				path: "/_/configuration/integration/vectorsurv/equipment",
				name: "Vectorsurv Equipment",
				component: ConfigurationIntegrationVectorsurvEquipment,
			},
			{
				path: "/_/configuration/integration/vectorsurv/sync",
				name: "Vectorsurv Synchronization",
				component: ConfigurationIntegrationVectorsurvSync,
			},
			{
				path: "/_/configuration/integration/veemac",
				name: "Veemac Integration Configuration",
				component: ConfigurationIntegrationVeemac,
			},
			{
				path: "/_/configuration/links",
				name: "Link Manager",
				component: ConfigurationLinks,
			},
			{
				path: "/_/configuration/organization",
				name: "Organization Configuration",
				component: ConfigurationOrganization,
			},
			{
				path: "/_/configuration/notifications",
				name: "Notification Configuration",
				component: ConfigurationNotifications,
			},
			{
				// The district's species list: the shared catalog it adopts its
				// local set from, the entries already adopted, the local species
				// no catalog entry matches, and a suggestion for one the catalog
				// does not carry (design D23, D25, D26).
				path: "/_/configuration/species",
				name: "Species Configuration",
				component: ConfigurationSpecies,
			},
			{
				// The district's tag vocabulary and symbol library now live inside
				// the Organization settings page, as sections under Site Fabric (both
				// are district configuration, so they sit with the rest of it rather
				// than owning nav entries). Only the tag detail screen keeps a route,
				// reached from the tag table in that section.
				component: ConfigurationTagDetail,
				name: "Tag Detail",
				path: "/_/configuration/tags/:id",
				props: (route) => ({
					id: parseInt(route.params.id as string, 10),
				}),
				meta: { role: "account-owner" },
			},
			{
				// The member's own settings. Not org-admin: an override is one
				// person's view (§5.2), so every member may open this.
				path: "/_/configuration/preferences",
				name: "Preferences",
				component: ConfigurationPreferences,
			},
			{
				path: "/_/configuration/upload",
				name: "Upload Configuration",
				component: ConfigurationUpload,
			},
			{
				component: ConfigurationUploadDetail,
				name: "Upload Detail",
				path: "/_/configuration/upload/:id",
				props: (route) => ({
					id: parseInt(route.params.id as string, 10),
				}),
			},
			{
				path: "/_/configuration/upload/pool",
				name: "Pool Upload",
				component: ConfigurationUploadPool,
			},
			{
				path: "/_/configuration/upload/pool/custom",
				name: "Custom Pool Upload",
				component: ConfigurationUploadPoolCustom,
			},
			{
				path: "/_/configuration/upload/pool/flyover",
				name: "Flyover Upload",
				component: ConfigurationUploadPoolFlyover,
			},
			{
				path: "/_/configuration/user",
				name: "User Configuration",
				component: ConfigurationUser,
			},
			{
				path: "/_/configuration/user/add",
				name: "User Add Configuration",
				component: ConfigurationUserAdd,
			},
			{
				component: ConfigurationUserEdit,
				name: "User Edit",
				path: "/_/configuration/user/:id",
				props: (route) => ({
					id: parseInt(route.params.id as string, 10),
				}),
			},
			{
				// Tracking devices: the caller's own Traccar-compatible ingest
				// credentials — enrol one, see when it last reported, withdraw it.
				// Not org-admin: a member enrols the credential for their own phone
				// (doc/in-progress/traccar.md §9.3).
				path: "/_/configuration/tracking-devices",
				name: "Tracking Devices",
				component: ConfigurationTrackingDevices,
			},
			{
				path: "/_/dash",
				name: "Dash",
				component: Dash,
			},
			{
				path: "/_/voice",
				name: "Voice",
				component: Voice,
			},
			{
				path: "/_/voice/help",
				name: "Voice Help",
				component: VoiceHelp,
			},
			{
				path: "/_/intelligence",
				component: LicenseGateRoute,
				meta: { license: "core" },
				children: [
					{
						path: "",
						name: "Intelligence",
						component: Intelligence,
					},
					{
						path: "invasive-aedes",
						name: "Intelligence Invasive Aedes",
						component: InvasiveAedes,
					},
				],
			},
			{
				path: "/_/oauth/refresh/arcgis",
				name: "Arcgis OAuth Refresh",
				component: OAuthRefreshArcgis,
			},
			// The trap-administration section is one nested route: Root.vue renders
			// the section header and the surface tabs, and the routed view renders
			// beneath them — the same shell the inventory section uses. The default
			// surface (Instances) is the existing trap-instance list/create; the
			// rest are the administration surfaces. The old flat paths
			// (/_/traps/configurations|labels) redirect so existing links do not 404.
			{
				path: "/_/traps",
				component: TrapsRoot,
				children: [
					{
						path: "",
						name: "Traps",
						component: TrapInstances,
					},
					// One Trap Instance (§24) and what it produced, reached from the
					// table on /_/traps. It is a distinct route rather than a panel
					// because the instance is the surface a printed QR is checked
					// against (§7.2, Q-T1): the same id is the address the code's
					// office-side mint and this page both key on.
					{
						path: ":id",
						name: "TrapInstance",
						component: TrapInstance,
					},
					// The district's Trap Stations (§23): the map of the standing
					// trapping network and the parcel-first Add station flow, then one
					// station's own review page. The list path is static, so it is
					// matched ahead of the bare `:id` instance route.
					{
						path: "stations",
						name: "Trap Stations",
						component: TrapStations,
					},
					{
						path: "stations/:id",
						name: "Trap Station",
						component: TrapStationDetail,
					},
					{
						path: "configuration",
						name: "Trap Configuration",
						component: Configurations,
					},
					{
						path: "sample-labels",
						name: "Trap Sample Labels",
						component: TrapSampleLabels,
					},
					{
						path: "devices",
						name: "Trap Devices",
						component: TrapDevices,
					},
					{
						path: "status",
						name: "Trap Status",
						component: TrapStatus,
					},
					// Redirects for the pre-tab paths, so old links land on the new
					// surfaces instead of 404ing.
					{ path: "configurations", redirect: "/_/traps/configuration" },
					{ path: "labels", redirect: "/_/traps/sample-labels" },
				],
			},
			{
				path: "/_/review",
				name: "Review",
				component: ReviewRoot,
			},
			{
				path: "/_/review/contact",
				name: "Contact Review",
				component: ReviewContact,
			},
			{
				path: "/_/review/mailer",
				name: "Mailer Review",
				component: ReviewMailer,
			},
			{
				path: "/_/review/inspection",
				name: "Inspection Review",
				component: ReviewInspectionList,
			},
			{
				path: "/_/review/inspection/:id",
				name: "Inspection Review Detail",
				component: ReviewInspectionDetail,
				props: (route) => ({
					id: route.params.id as string,
				}),
			},
			{
				path: "/_/review/treatment",
				name: "Treatment Review",
				component: ReviewTreatmentList,
			},
			{
				path: "/_/review/treatment/:id",
				name: "Treatment Review Detail",
				component: ReviewTreatmentDetail,
				props: (route) => ({
					id: route.params.id as string,
				}),
			},
			{
				path: "/_/review/pool",
				name: "Pool Review",
				component: ReviewPool,
			},
			{
				path: "/_/review/pools/manual",
				name: "Manual Pool Review",
				component: ManualPoolReview,
			},
			{
				component: ReviewFeature,
				name: "Feature Review",
				path: "/_/review/feature/:id",
				props: (route) => ({
					id: route.params.id as string,
				}),
			},
			{
				path: "/_/review/site",
				name: ROUTE_NAMES.REVIEW_SITE,
				component: ReviewSite,
			},
			{
				path: "/_/review/site/:id",
				name: ROUTE_NAMES.SITE_DETAIL,
				component: SiteDetail,
				props: (route) => ({
					id: parseInt(route.params.id as string, 10),
				}),
			},
			{
				path: "/_/sudo/planet/order/:id",
				name: "Planet Order Detail",
				component: () => import("@/view/sudo/PlanetDetail.vue"),
			},
			{
				path: "/_/sudo/planet/item/:id",
				name: "Planet Item Detail",
				component: () => import("@/view/sudo/PlanetItemDetail.vue"),
			},
			{
				path: "/_/sudo/planet/mosaic-subscription/:id",
				name: "Planet Mosaic Subscription Detail",
				component: () => import("@/view/sudo/PlanetMosaicDetail.vue"),
			},
			{
				path: "/_/sudo/planet/mosaic-subscription",
				name: "Planet Mosaic Subscriptions",
				component: () => import("@/view/sudo/PlanetMosaic.vue"),
			},
			{
				path: "/_/sudo/planet/mosaic",
				name: "Planet Mosaics",
				component: () => import("@/view/sudo/PlanetMosaicExplore.vue"),
			},
			{
				path: "/_/sudo/planet/align",
				name: "Planet Alignment",
				component: () => import("@/view/sudo/PlanetAlign.vue"),
			},
			{
				path: "/_/sudo/planet/align/compare/:itemId",
				name: "Planet Alignment Compare",
				component: () => import("@/view/sudo/PlanetAlignCompare.vue"),
			},
			{
				path: "/_/sudo/planet/order",
				name: "Planet Orders",
				component: () => import("@/view/sudo/PlanetOrder.vue"),
			},
			{
				path: "/_/sudo/planet/item",
				name: "Planet Items",
				component: () => import("@/view/sudo/PlanetItem.vue"),
			},
			{
				path: "/_/sudo/parcel/source/:id",
				name: "Parcel Detail",
				component: () => import("@/view/sudo/ParcelDetail.vue"),
			},
			{
				path: "/_/sudo/situs/new",
				name: "New Situs Source",
				component: () => import("@/view/sudo/SitusNew.vue"),
			},
			{
				path: "/_/sudo/situs/source/:id",
				name: "Situs Detail",
				component: () => import("@/view/sudo/SitusDetail.vue"),
			},
			{
				path: "/_/sudo/orgs/:id",
				name: "Org Detail",
				component: () => import("@/view/sudo/OrgDetail.vue"),
			},
			{
				path: "/_/sudo/reveal/:id",
				name: "Reveal Detail",
				component: () => import("@/view/sudo/RevealDetail.vue"),
			},
			{
				path: "/_/sudo/reveal/:orgid/upload",
				name: "Reveal Upload Manual Analysis",
				component: () => import("@/view/sudo/UploadManualAnalysis.vue"),
			},
			{
				path: "/_/sudo/reveal/:orgid/analysis/:analysisid",
				name: "Reveal Analysis Detail",
				component: () => import("@/view/sudo/RevealAnalysisDetail.vue"),
			},
			{
				path: "/_/sudo/reveal/:orgid/analysis/:analysisid/review/:taskid",
				name: "Reveal Review",
				component: () => import("@/view/sudo/RevealReview.vue"),
			},
			{
				path: "/_/sudo/reveal/:orgid/analysis/:analysisid/section",
				name: "Reveal Analysis Section",
				component: () => import("@/view/sudo/RevealAnalysisSection.vue"),
			},
			{
				path: "/_/sudo/vision/item/:id",
				name: "Vision Item Detail",
				component: () => import("@/view/sudo/VisionDetail.vue"),
			},
			{
				path: "/_/sudo/vision/item/:itemId/pool/:poolId",
				name: "Vision Pool Debug",
				component: () => import("@/view/sudo/VisionPoolDebug.vue"),
			},
			{
				path: "/_/sudo/vision/item/:itemId/inference",
				name: "Vision Inference",
				component: () => import("@/view/sudo/VisionInference.vue"),
			},
			{
				path: "/_/sudo/vision/analysis/:id",
				name: "Vision Analysis Detail",
				component: () => import("@/view/sudo/VisionAnalysis.vue"),
			},
			{
				path: "/_/sudo/vision/pool",
				name: "Vision Pool List",
				component: () => import("@/view/sudo/VisionPoolList.vue"),
			},
			{
				path: "/_/sudo/vision/corpus",
				name: "Vision Training Corpus",
				component: () => import("@/view/sudo/VisionCorpus.vue"),
			},
			{
				path: "/_/sudo/vision/corpus/item/:itemId",
				name: "Vision Corpus Item",
				component: () => import("@/view/sudo/VisionCorpusItem.vue"),
			},
			{
				path: "/_/sudo/vision/corpus/pool/:poolId",
				name: "Vision Corpus Pool",
				component: () => import("@/view/sudo/VisionCorpusPool.vue"),
			},
			{
				path: "/_/sudo/vision/inference",
				name: "Vision Inference List",
				component: () => import("@/view/sudo/VisionInferenceList.vue"),
			},
			{
				path: "/_/sudo/vision/analysis-review",
				name: "Vision Analysis List",
				component: () => import("@/view/sudo/VisionAnalysisList.vue"),
			},
			{
				path: "/_/sudo/vision/analysis-review/:id",
				name: "Vision Analysis Review Detail",
				component: () => import("@/view/sudo/VisionInferenceAnalysis.vue"),
			},
			{
				path: "/_/sudo/vision/manifest/:id/area/create",
				name: "Vision Manifest Area Create",
				component: () => import("@/view/sudo/VisionManifestAreaCreate.vue"),
			},
			{
				path: "/_/sudo/vision/manifest-area/:id",
				name: "Vision Manifest Area Detail",
				component: () => import("@/view/sudo/VisionManifestAreaDetail.vue"),
			},
			{
				path: "/_/sudo/vision/manifest/create",
				name: "Vision Manifest Create",
				component: () => import("@/view/sudo/VisionManifestCreate.vue"),
			},
			{
				path: "/_/sudo/vision/manifest-sample/:id",
				name: "Vision Manifest Sample Detail",
				component: () => import("@/view/sudo/VisionManifestSampleDetail.vue"),
			},
			{
				path: "/_/sudo/vision/analysis-task",
				name: "Vision Analysis Task",
				component: () => import("@/view/sudo/VisionRowLookup.vue"),
				props: {
					title: "Analysis Task",
					detailPath: "/_/sudo/vision/analysis-task",
				},
			},
			{
				path: "/_/sudo/vision/analysis-pool",
				name: "Vision Analysis Pool",
				component: () => import("@/view/sudo/VisionRowLookup.vue"),
				props: {
					title: "Analysis Pool",
					detailPath: "/_/sudo/vision/analysis-pool",
				},
			},
			{
				path: "/_/sudo/vision/analysis-pool/:id",
				name: "Vision Analysis Pool Detail",
				component: () => import("@/view/sudo/VisionRowDetail.vue"),
				props: {
					title: "Analysis Pool",
					endpoint: "/api/sudo/training/analysis-pool",
				},
			},
			{
				path: "/_/sudo/vision/analysis-task-pool",
				name: "Vision Analysis Task Pool",
				component: () => import("@/view/sudo/VisionRowLookup.vue"),
				props: {
					title: "Analysis Task Pool",
					detailPath: "/_/sudo/vision/analysis-task-pool",
				},
			},
			{
				path: "/_/sudo/vision/analysis-task-pool/:id",
				name: "Vision Analysis Task Pool Detail",
				component: () => import("@/view/sudo/VisionRowDetail.vue"),
				props: {
					title: "Analysis Task Pool",
					endpoint: "/api/sudo/training/analysis-task-pool",
					tegolaLayer: "vision-analysis-task-pool",
				},
			},
			{
				path: "/_/sudo/vision/analysis-task/:id",
				name: "Vision Analysis Task Detail",
				component: () => import("@/view/sudo/VisionAnalysisTaskDetail.vue"),
			},
			{
				path: "/_/sudo/vision/deduplication",
				name: "Vision Deduplication",
				component: () => import("@/view/sudo/VisionRowLookup.vue"),
				props: {
					title: "Deduplication",
					detailPath: "/_/sudo/vision/deduplication",
				},
			},
			{
				path: "/_/sudo/vision/deduplication/:id",
				name: "Vision Deduplication Detail",
				component: () => import("@/view/sudo/VisionDeduplicate.vue"),
			},
			{
				path: "/_/sudo/vision/manifest/:id",
				name: "Vision Manifest Detail",
				component: () => import("@/view/sudo/VisionManifestDetail.vue"),
			},
			{
				path: "/_/sudo/vision/manifest",
				name: "Vision Manifest List",
				component: () => import("@/view/sudo/VisionManifestList.vue"),
			},
			{
				path: "/_/sudo/vision/run",
				name: "Vision Run List",
				component: () => import("@/view/sudo/VisionRunList.vue"),
			},
			{
				path: "/_/sudo/vision",
				name: "Vision",
				component: Vision,
			},
			{
				path: "/_/sudo/gpu/auto-scale",
				name: "Sudo GPU Auto-Scale",
				component: () => import("@/view/sudo/AutoScale.vue"),
			},
			{
				path: "/_/sudo/gpu/pricing",
				name: "Sudo GPU Pricing",
				component: () => import("@/view/sudo/Pricing.vue"),
			},
			{
				path: "/_/sudo/gpu/launch",
				name: "Sudo GPU Launch",
				component: () => import("@/view/sudo/GpuLaunch.vue"),
			},
			{
				path: "/_/sudo/gpu/:worker_id",
				name: "GPU Worker Detail",
				component: () => import("@/view/sudo/WorkerDetail.vue"),
			},
			{
				path: "/_/sudo/background",
				name: "Sudo Background",
				component: () => import("@/view/sudo/Background.vue"),
			},
			{
				path: "/_/sudo/background/queue",
				name: "Sudo Background Queue",
				component: () => import("@/view/sudo/BackgroundQueue.vue"),
			},
			{
				path: "/_/sudo/background/cron",
				name: "Sudo Background Cron Jobs",
				component: () => import("@/view/sudo/BackgroundCron.vue"),
			},
			{
				path: "/_/sudo/background/statistics",
				name: "Sudo Background Statistics",
				component: () => import("@/view/sudo/BackgroundStatistics.vue"),
			},
			{
				path: "/_/sudo/background/history",
				name: "Sudo Background History",
				component: () => import("@/view/sudo/BackgroundHistory.vue"),
			},
			{
				path: "/_/sudo/maintenance",
				name: "Sudo Maintenance",
				component: () => import("@/view/sudo/Maintenance.vue"),
			},
			{
				path: "/_/sudo/ask-log",
				name: "Sudo Ask Log",
				component: () => import("@/view/sudo/AskLog.vue"),
			},
			{
				path: "/_/sudo/pronunciation",
				name: "Sudo Pronunciation Review",
				component: () => import("@/view/sudo/pronunciation/Sessions.vue"),
				meta: { role: "root" },
			},
			{
				path: "/_/sudo/pronunciation/:uuid",
				name: "Sudo Pronunciation Review Detail",
				component: () => import("@/view/sudo/pronunciation/SessionDetail.vue"),
				meta: { role: "root" },
			},
			{
				path: "/_/sudo",
				name: "Sudo",
				component: () => import("@/view/sudo/Root.vue"),
			},
			{
				path: "/_/sudo/catalog",
				name: "Sudo Catalog",
				component: () => import("@/view/sudo/Catalog.vue"),
			},
			{
				path: "/_/sudo/catalog/imports",
				name: "Sudo Catalog Imports",
				component: () => import("@/view/sudo/CatalogImports.vue"),
			},
			{
				path: "/_/sudo/catalog/imports/:caRegNo",
				name: "Sudo Catalog Import Detail",
				component: () => import("@/view/sudo/CatalogImportDetail.vue"),
			},
			{
				path: "/_/sudo/catalog/requests",
				name: "Sudo Catalog Requests",
				component: () => import("@/view/sudo/CatalogRequests.vue"),
			},
			{
				path: "/_/sudo/catalog/brands/:id",
				name: "Sudo Catalog Brand Detail",
				component: () => import("@/view/sudo/CatalogBrand.vue"),
			},
			{
				path: "/_/sudo/distributors",
				name: "Sudo Distributors",
				component: () => import("@/view/sudo/Distributors.vue"),
			},
			{
				path: "/_/sudo/distributors/:id",
				name: "Sudo Distributor Detail",
				component: () => import("@/view/sudo/DistributorDetail.vue"),
			},
			{
				path: "/_/sudo/supply-chain",
				name: "Sudo Supply Chain",
				component: () => import("@/view/sudo/SupplyChain.vue"),
			},
			{
				path: "/_/sudo/catalog/new",
				name: "Sudo Catalog New",
				component: () => import("@/view/sudo/CatalogDetail.vue"),
			},
			{
				path: "/_/sudo/catalog/:id",
				name: "Sudo Catalog Detail",
				component: () => import("@/view/sudo/CatalogDetail.vue"),
			},
			{
				path: "/_/sudo/naip",
				name: "Sudo NAIP Explorer",
				component: () => import("@/view/sudo/NaipExplorer.vue"),
			},
			{
				path: "/_/sudo/comms",
				name: "Sudo Comms",
				component: () => import("@/view/sudo/Comms.vue"),
			},
			{
				path: "/_/sudo/planet",
				name: "Sudo Planet",
				component: () => import("@/view/sudo/Planet.vue"),
			},
			{
				path: "/_/sudo/parcel",
				name: "Sudo Parcel",
				component: () => import("@/view/sudo/Parcel.vue"),
			},
			{
				path: "/_/sudo/situs",
				name: "Sudo Situs",
				component: () => import("@/view/sudo/Situs.vue"),
			},
			{
				path: "/_/sudo/tiger",
				name: "Sudo TIGER",
				component: () => import("@/view/sudo/Tiger.vue"),
			},
			{
				path: "/_/sudo/stadia",
				name: "Sudo Stadia",
				component: () => import("@/view/sudo/Stadia.vue"),
			},
			{
				path: "/_/sudo/gpu",
				name: "Sudo GPU",
				component: () => import("@/view/sudo/Gpu.vue"),
			},
			{
				path: "/_/sudo/ui",
				name: "Sudo UI",
				component: () => import("@/view/sudo/UI.vue"),
			},
			{
				path: "/_/sudo/reveal",
				name: "Sudo Reveal",
				component: () => import("@/view/sudo/Reveal.vue"),
			},
			{
				path: "/_/sudo/orgs",
				name: "Sudo Orgs",
				component: () => import("@/view/sudo/Orgs.vue"),
			},
			{
				path: "/_/sudo/vision-tasks",
				name: "Vision Task Review",
				component: SudoVisionTaskOverview,
			},
			{
				path: "/_/sudo/vision-tasks/pool-corpus",
				name: "Pool Corpus Review",
				component: SudoVisionTaskPoolCorpus,
			},
			{
				path: "/_/sudo/vision-tasks/pool-corpus/item/:itemId",
				name: "Vision Task Item Review",
				component: SudoVisionTaskItem,
			},
			{
				path: "/_/sudo/vision-tasks/pool-corpus/item/:itemId/:z/:x/:y",
				name: "Vision Task Detail Review",
				component: SudoVisionTaskReview,
			},
			{
				path: "/_/sudo/vision-tasks/position-inference-anchoring",
				name: "Position Inference Anchoring Review",
				component: SudoVisionTaskPositionInference,
			},
			{
				path: "/_/sudo/vision-tasks/position-inference-anchoring/:poolId",
				name: "Position Inference Anchoring Pool Review",
				component: SudoVisionTaskPositionInference,
			},
			{
				path: "/_/sudo/vision-tasks/tile-confirmation",
				name: "Tile Confirmation",
				component: SudoTileConfirmation,
			},
			{
				path: "/_/sudo/vision-tasks/tile-confirmation/:taskId",
				name: "Tile Confirmation Task",
				component: SudoTileConfirmation,
			},
			{
				path: "/_/sudo/vision-tasks/correct-finding",
				name: "Correct Finding Review",
				component: SudoCorrectFinding,
			},
			{
				path: "/_/sudo/vision-tasks/correct-finding/:id",
				name: "Correct Finding Detail Review",
				component: SudoCorrectFinding,
			},
			{
				path: "/_/sudo/vision-tasks/pool-condition",
				name: "Pool Condition Review",
				component: SudoConditionReview,
			},
			{
				path: "/_/sudo/vision-tasks/pool-condition/:id",
				name: "Pool Condition Review Detail",
				component: SudoConditionReview,
			},
			{
				path: "/_/ui",
				name: "UI Playground",
				component: UI,
			},
			// The inventory section is one nested route: Root.vue renders the
			// surface tabs and the routed view beneath them, so every surface is
			// reachable from any other. Flat sibling routes would never render
			// the shell and would hide every surface but the one typed in.
			{
				path: "/_/inventory",
				component: InventoryRoot,
				children: [
					{
						path: "",
						name: "Inventory",
						component: Inventory,
					},
					{
						path: "products",
						name: "Inventory Products",
						component: InventoryProducts,
					},
					{
						path: "products/:id",
						name: "Inventory Product Detail",
						component: InventoryProductDetail,
					},
					{
						path: "catalog",
						name: "Inventory Catalog",
						component: InventoryCatalog,
					},
					{
						path: "catalog/:id",
						name: "Inventory Catalog Detail",
						component: InventoryCatalogDetail,
					},
					{
						path: "interventions",
						name: "Inventory Interventions",
						component: InventoryInterventions,
					},
					{
						path: "receive",
						name: "Inventory Receive",
						component: InventoryReceive,
					},
					{
						path: "movements",
						name: "Inventory Movements",
						component: InventoryMovements,
					},
					{
						path: "containers",
						name: "Inventory Containers",
						component: InventoryContainers,
					},
					{
						path: "locations",
						name: "Inventory Locations",
						component: InventoryLocations,
					},
					{
						path: "checks",
						name: "Inventory Checks",
						component: InventoryChecks,
					},
					{
						path: "checks/:id",
						name: "Inventory Check Detail",
						component: InventoryCheckDetail,
					},
					{
						path: "reconciliation",
						name: "Inventory Reconciliation",
						component: InventoryReconciliation,
					},
					{
						path: "units",
						name: "Inventory Units",
						component: InventoryUnits,
					},
					{
						path: "mixes",
						name: "Inventory Mixes",
						component: InventoryMixes,
					},
					{
						path: "mixes/:id",
						name: "Inventory Mix Detail",
						component: InventoryMixDetail,
					},
				],
			},
			{
				path: "/_/sudo/valhalla",
				name: "Sudo Valhalla",
				component: () => import("@/view/sudo/valhalla/ValhallaOverview.vue"),
			},
			{
				path: "/_/sudo/valhalla/test",
				name: "Valhalla Routing Test",
				component: () => import("@/view/sudo/valhalla/ValhallaTest.vue"),
			},
			{
				path: "/_/sudo/valhalla/builds/:buildId",
				name: "Valhalla Build",
				component: () => import("@/view/sudo/valhalla/ValhallaBuild.vue"),
			},
		],
		component: Authenticated,
		path: "/_",
		name: "Authenticated",
	},
	{
		children: [
			{
				path: "",
				component: LicenseGateRoute,
				meta: { license: "reveal" },
				children: [
					{
						path: "",
						component: () => import("@/view/Reveal.vue"),
					},
				],
			},
		],
		component: FullPage,
		path: "/_/reveal",
		name: ROUTE_NAMES.REVEAL,
	},
	{
		children: [
			{
				path: "",
				component: LicenseGateRoute,
				meta: { license: "core" },
				children: [
					{
						path: "",
						component: Planning,
					},
				],
			},
		],
		component: FullPage,
		path: "/_/planning",
		name: "Planning",
	},
	{
		// The Operations workbench is a full-page map with its own tool rail and
		// bottom sheet (like /_/planning and /_/reveal). It must sit outside the
		// Authenticated shell: that shell renders the navbar, which would sit over
		// the rail and the details sheet.
		children: [
			{
				path: "",
				component: LicenseGateRoute,
				meta: { license: "core" },
				children: [
					{
						path: "",
						name: "Operations",
						component: Operations,
					},
				],
			},
		],
		component: FullPage,
		path: "/_/operations",
	},
	{
		// Full-page map, no navbar shell (like /_/planning and /_/reveal).
		children: [
			{
				path: "",
				name: "Note Review",
				component: ReviewTranscripts,
			},
		],
		component: FullPage,
		path: "/_/review/note",
	},
	{
		children: [
			{
				path: "",
				component: () => import("@/view/sudo/iosmock/IosMockShell.vue"),
				children: [
					{
						path: "",
						name: "iOS Mock Index",
						component: () => import("@/view/sudo/iosmock/IosMockIndex.vue"),
					},
					{
						path: "survey",
						name: "iOS Mock Survey Session",
						component: () =>
							import("@/view/sudo/iosmock/screens/SurveySession.vue"),
					},
					{
						path: "initial",
						name: "iOS Mock Initial State",
						component: () =>
							import("@/view/sudo/iosmock/screens/InitialState.vue"),
					},
					{
						path: "feature",
						name: "iOS Mock Feature Observation",
						component: () =>
							import("@/view/sudo/iosmock/screens/FeatureObservation.vue"),
					},
					{
						path: "inspection",
						name: "iOS Mock Inspection",
						component: () =>
							import("@/view/sudo/iosmock/screens/Inspection.vue"),
					},
					{
						path: "treatment",
						name: "iOS Mock Treatment",
						component: () =>
							import("@/view/sudo/iosmock/screens/Treatment.vue"),
					},
					{
						path: "safety",
						name: "iOS Mock Safety Assessment",
						component: () =>
							import("@/view/sudo/iosmock/screens/SafetyAssessment.vue"),
					},
					{
						path: "access",
						name: "iOS Mock Access Assessment",
						component: () =>
							import("@/view/sudo/iosmock/screens/AccessAssessment.vue"),
					},
					{
						path: "complete",
						name: "iOS Mock Session Complete",
						component: () =>
							import("@/view/sudo/iosmock/screens/SessionComplete.vue"),
					},
				],
			},
		],
		component: FullPage,
		path: "/_/sudo/ios-mock",
	},
	{
		children: [
			{
				path: "",
				component: ReviewMapvisionIndex,
			},
			{
				path: "map",
				component: FullPage,
				children: [
					{
						path: "",
						name: "MapVision Map",
						component: ReviewMapvisionMap,
					},
				],
			},
			{
				path: "type/:type",
				name: "MapVision Type List",
				component: ReviewMapvisionTypeList,
			},
			{
				path: "type/:type/:id",
				name: "MapVision Type Detail",
				component: ReviewMapvisionTypeDetail,
			},
			{
				path: "activity-card",
				component: ReviewMapvisionActivityCard,
			},
		],
		path: "/_/review/mapvision",
		name: "MapVision Review",
	},
	{
		component: Signin,
		name: "Signin",
		path: "/signin",
	},
	{
		component: Signout,
		name: "Signout",
		path: "/signout",
	},
	{
		component: Signup,
		name: "Signup",
		path: "/signup",
	},
	{
		component: ForgotPassword,
		name: "ForgotPassword",
		path: "/forgot-password",
	},
	{
		component: ResetPassword,
		name: "ResetPassword",
		path: "/reset-password/:uuid/:email",
	},
	// The Review section's Map: the full-page Map Review map workbench, no navbar
	// shell (like /_/planning, /_/reveal and /_/review/note). Reached from the
	// Map card on /_/review. FullPage establishes the session before the view
	// mounts.
	{
		children: [
			{
				path: "",
				component: MapReview,
			},
		],
		component: FullPage,
		path: "/_/review/map",
		name: "Map",
	},
	// The map lab: an additive, full-page map comparison bench for picking a
	// spatial encoding and a layer tray. It is lazily imported so the workbench
	// bundle does not carry a comparison bench's views.
	{
		children: [
			{
				path: "",
				component: () => import("@/view/MapLab.vue"),
			},
		],
		component: FullPage,
		path: "/_/map-lab",
		name: "Map Lab",
	},
	// Nidus Lab: the trap-sample processing workbench, and the hub in front of it
	// (doc/in-progress/nidus-notes/lab-sample-processing.md). The lab-facing half of
	// the trap family — identify a collected sample, record its composition and
	// its pools, review and submit. `/_/lab` is the hub: one card per surface,
	// each surface a child path of its own, so the workbench keeps the path it
	// has always had and a new surface is a card plus a child, not a new
	// top-level route. Lazily imported, like the map lab, so the ledger bundle
	// does not carry a bench's views, and licensed `core` like the rest of the
	// trap surfaces.
	{
		children: [
			{
				path: "",
				component: LicenseGateRoute,
				meta: { license: "core" },
				children: [
					{
						path: "",
						component: () => import("@/view/LabHome.vue"),
					},
					{
						path: "counts",
						component: () => import("@/view/Lab.vue"),
					},
					{
						path: "pools",
						component: () => import("@/view/LabPools.vue"),
					},
					// The trap view: the lab's map workbench, a child of the
					// bench's own path so the workbench keeps the path it has
					// always had and a new surface is a child, not a new
					// top-level route.
					{
						path: "records",
						component: () => import("@/view/LabRecords.vue"),
					},
				],
			},
		],
		component: FullPage,
		path: "/_/lab",
		name: "Lab",
	},
	// Catch-all route - must be last
	{
		path: "/:pathMatch(.*)*",
		name: "NotFound",
		component: NotFound,
	},
];

export const router = createRouter({
	history: createWebHistory("/"),
	routes,
});

// Global navigation guard
router.beforeEach(async (to) => {
	if (
		to.fullPath.startsWith("/signin") ||
		to.fullPath == "/signup" ||
		to.fullPath == "/forgot-password" ||
		to.fullPath.startsWith("/reset-password/")
	) {
		return;
	}
	const storeSession = useSessionStore();
	// While the sync maintenance gate is armed, an already-authenticated
	// non-root session stays on its route: Authenticated swaps the shell for
	// the maintenance lockout screen (the gate 503s its API calls anyway).
	// Visitors without a session still take the normal signin redirect below.
	const syncGateActive =
		storeSession.maintenance?.gates.sync.active === true &&
		storeSession.self?.role !== "root" &&
		storeSession.isAuthenticated;
	if (syncGateActive) {
		return;
	}
	try {
		if (!storeSession.isLoading && !storeSession.isAuthenticated) {
			// The link being followed is `to`, not `from`: on a cold load there is
			// no previous route, and a shared workbench URL carries a query string
			// (`state`, a `spec` payload) that must survive the sign-in round trip
			// with its own escaping. A router location hands the whole fullPath to
			// the query encoder once, where interpolating it into a string would
			// escape it twice and mangle a `?`, `&` or `#` inside a filter clause.
			return { path: "/signin", query: { next: to.fullPath } };
		}
	} catch (error) {
		console.log("check auth failed");
	}
	// The Sudo area is role-scoped: root opens every tool, staff the shared
	// catalog surface and the vision-tasks tool, a vision-reviewer the
	// vision-tasks tool only. The server enforces the same boundary per handler;
	// this keeps a role that cannot use a tool from landing on its page.
	if (
		!storeSession.isLoading &&
		storeSession.isAuthenticated &&
		to.path.startsWith("/_/sudo") &&
		!canOpenSudoPath(storeSession.self?.role, to.path)
	) {
		return "/_/dash";
	}
	// A root-only screen (the pronunciation review) resolves the session before
	// deciding: the Sudo gate above deliberately lets a not-yet-loaded session
	// through, which would flash the page for a non-root cold load. The server
	// refuses every one of its APIs anyway; this keeps the cosmetic gate honest.
	if (to.meta.role === "root") {
		try {
			await storeSession.get();
		} catch {
			// The auth branch above already handled an absent session; a failed
			// probe here falls through to the client-side gate below.
		}
		if (storeSession.self != null && storeSession.self.role !== "root") {
			return "/_/dash";
		}
	}
	// The org-admin screens (§8.1) carry `meta: { role: "account-owner" }`. This
	// is a cosmetic gate: the server refuses the write regardless, and it exists
	// only so a member who is not an org admin is not handed a screen whose every
	// action would 403. Root passes always; the account-owner role passes on its
	// own district's routes (`account-owner`, platform.User.IsAccountOwner);
	// everyone else goes to the dash. A session that has not landed yet has no
	// role to judge — the store's first refresh is still in flight — so the
	// navigation is let through rather than bouncing an admin off their screen,
	// and the auth branch above has already handled a real absence of a session.
	if (to.meta.role === "account-owner" && storeSession.self != null) {
		const role = storeSession.self.role;
		if (role !== "root" && role !== "account-owner") {
			return "/_/dash";
		}
	}
	return;
});

export default router;
